import { Range } from 'domains/reporter/RichTextEditor/core';
import { stringifyRange } from 'domains/reporter/RichTextEditor/utils/stringify';

import { logger } from 'modules/logger';

export type MemoryInfo = {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
};

/**
 *
 * @param markName - name of the mark
 * @param id - unique identifier to append to the mark name
 * @returns  a mark name with the id appended (e.g., "markName-id")
 */
export const createMarkForId = (markName: string, id: Range | string): string =>
  `${markName}-${typeof id === 'string' ? id : stringifyRange(id)}`;

/**
 *
 * @param markName - name of the mark
 * @param id - unique identifier to append to the mark name
 * @returns the mark name with the id appended, creating the mark if it does not already exist
 */
export const markOnce = (markName: string, id: Range | string) => {
  const mark = createMarkForId(markName, id);
  if (!performance.getEntriesByName(mark).length) {
    performance.mark(mark);
  }

  return mark;
};

export const hasMark = (markName: string) => {
  return performance.getEntriesByName(markName).length > 0;
};

/**
 * Performance wraps methods from the performance API
 * as some of them can throw runtime exceptions.
 */
export const performance = {
  now(): number {
    return window.performance.now();
  },
  mark(name: string, id?: Range | string): undefined {
    if (id != null) {
      window.performance.mark(createMarkForId(name, id));
    } else {
      window.performance.mark(name);
    }
  },
  /**
   * Measure wraps the performance.measure method
   * to handle different argument forms.
   *
   * The startOrOptions parameter can be either a string (legacy form) or an object
   * containing start, end, duration, and details (options form), where details can store
   * any additional metadata related to the measurement.
   */
  measure(
    name: string,
    startOrOptions?: string | PerformanceMeasureOptions,
    endMark?: string
  ): undefined {
    try {
      if (typeof startOrOptions === 'object') {
        // options form: { start, end, detail, duration }
        window.performance.measure(name, startOrOptions);
      } else {
        // legacy form
        window.performance.measure(name, startOrOptions, endMark);
      }
    } catch (e: any) {
      logger.error('[performance] Error occurred while measuring performance', e);
    }
  },
  clearMarks(markName?: string): undefined {
    window.performance.clearMarks(markName);
  },
  clearMarksByName(markNames: string[]): undefined {
    markNames.forEach((mark) => performance.clearMarks(mark));
  },
  clearMeasures(measureName?: string): undefined {
    window.performance.clearMeasures(measureName);
  },
  hasEntry(name: string): boolean {
    return performance.getEntriesByName(name).length > 0;
  },
  getEntriesByName(name: string, type?: string): PerformanceEntryList {
    return window.performance.getEntriesByName(name, type);
  },
  getEntriesByType(type: string): PerformanceEntryList {
    return window.performance.getEntriesByType(type);
  },
  memory(): Nullable<MemoryInfo> {
    return (window.performance as Performance & { memory?: MemoryInfo }).memory;
  },
} as const;
